import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import {
  Search,
  UserX,
  UserCheck,
  Trash2,
  Pencil,
  KeyRound,
  Users as UsersIcon,
  Building2,
  ArrowLeft,
  MoreVertical,
  UserPlus,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import BulkDeleteDialog from "../../components/platform/BulkDeleteDialog";
import UserEditDialog from "../../components/platform/UserEditDialog";
import UserPasswordResetDialog from "../../components/platform/UserPasswordResetDialog";
import TenantAddUserDialog from "../../components/platform/TenantAddUserDialog";

/**
 * Platform Admin → "Kullanıcılar" (Users)
 *
 * Split-view layout redesigned per user's request (2026-02-14):
 *   ┌─────────────┬─────────────────────────────────────┐
 *   │ Tenants     │ Users of the selected tenant        │
 *   │ (searchable)│ (edit / reset-pw / activate / del)  │
 *   └─────────────┴─────────────────────────────────────┘
 *
 * Left column lists every tenant with an active user count and a
 * search filter. Clicking a tenant loads its user roster on the right
 * via `GET /platform/users?tenant_id=<id>`. The right panel keeps all
 * of the previous row-level admin controls plus per-user "Düzenle" and
 * "Şifreyi Sıfırla" actions surfaced via a shadcn `DropdownMenu`.
 */
export default function PlatformUsers() {
  const { t } = useTranslation();

  // ── LEFT PANE: tenants ────────────────────────────────────────────
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantLoading, setTenantLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState(null);

  // ── RIGHT PANE: users of selected tenant ──────────────────────────
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  // Bulk selection scoped to the currently visible tenant's users.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  // Per-user dialog state
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [addUserOpen, setAddUserOpen] = useState(false);

  // Load tenants once — the sidebar rarely changes and each tenant
  // fetch already carries `user_count` from the platform endpoint.
  const loadTenants = async () => {
    setTenantLoading(true);
    try {
      const { data } = await api.get("/platform/tenants");
      const list = data || [];
      setTenants(list);
      // Auto-select the first tenant so the right pane isn't empty
      // on first paint.
      if (!selectedTenantId && list.length > 0) {
        setSelectedTenantId(list[0].id);
      }
    } finally {
      setTenantLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const loadUsers = async () => {
    if (!selectedTenantId) {
      setUsers([]);
      return;
    }
    setUserLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("tenant_id", selectedTenantId);
      if (userSearch.trim()) params.set("q", userSearch.trim());
      if (roleFilter !== "all") params.set("role", roleFilter);
      if (activeFilter !== "all") params.set("active", activeFilter);
      params.set("limit", "500");
      const { data } = await api.get(`/platform/users?${params.toString()}`);
      setUsers(data.users || []);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIds(new Set()); // reset selection whenever filters change
    const timer = setTimeout(loadUsers, 200);
    return () => clearTimeout(timer);
  }, [selectedTenantId, userSearch, roleFilter, activeFilter]);

  const selectedTenant = useMemo(
    () => tenants.find((tn) => tn.id === selectedTenantId) || null,
    [tenants, selectedTenantId]
  );

  const filteredTenants = useMemo(() => {
    const s = tenantSearch.trim().toLowerCase();
    if (!s) return tenants;
    return tenants.filter(
      (tn) =>
        (tn.name || "").toLowerCase().includes(s) ||
        (tn.slug || "").toLowerCase().includes(s) ||
        (tn.id || "").toLowerCase().includes(s)
    );
  }, [tenants, tenantSearch]);

  // ── Row-level user mutations ──────────────────────────────────────
  const setActive = async (u, next) => {
    try {
      await api.patch(`/platform/users/${u.id}`, { active: next });
      toast.success(next ? t("platform.users.activated") : t("platform.users.deactivated"));
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/platform/users/${u.id}`, { role });
      toast.success(t("platform.users.role_updated"));
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ── Bulk selection helpers ────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (all) => {
    if (all) setSelectedIds(new Set(users.map((u) => u.id)));
    else setSelectedIds(new Set());
  };

  const runBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const { data } = await api.post("/platform/users/bulk-delete", { ids });
    const removed = data.deleted_users || 0;
    const skipped = data.skipped || [];
    if (removed) toast.success(`${removed} kullanıcı silindi`);
    if (skipped.length) {
      const reasons = new Set(skipped.map((s) => s.reason));
      const parts = [];
      if (reasons.has("platform_admin_protected")) parts.push("platform admin korumalı");
      if (reasons.has("cannot_delete_self")) parts.push("kendinizi silemezsiniz");
      if (reasons.has("last_super_admin")) parts.push("son super admin korunur");
      toast.warning(`${skipped.length} kullanıcı silinemedi (${parts.join(", ") || "korumalı"})`);
    }
    setSelectedIds(new Set());
    loadUsers();
    // The user_count on the left tenant chip is now stale — refresh.
    loadTenants();
  };

  const deleteOne = async (u) => {
    if (!window.confirm(`${u.name || u.email} kullanıcısı ve içeriği kalıcı olarak silinsin mi?`)) return;
    const { data } = await api.post("/platform/users/bulk-delete", { ids: [u.id] });
    if (data.deleted_users) {
      toast.success("Kullanıcı silindi");
      loadUsers();
      loadTenants();
    } else if (data.skipped?.length) {
      const reason = data.skipped[0].reason;
      const map = {
        platform_admin_protected: "Platform admin silinemez",
        cannot_delete_self: "Kendinizi silemezsiniz",
        last_super_admin: "Son super admin silinemez",
      };
      toast.warning(map[reason] || "Silinemedi");
    }
  };

  return (
    <div className="space-y-4" data-testid="platform-users">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-600">
          Serpix Admin
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("platform.nav.users")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Sol taraftan bir tenant seç, sağda o tenant&apos;ın kullanıcılarını yönet.
        </p>
      </header>

      {/* Split view — 360px sidebar on ≥lg, stacked on mobile */}
      <div className="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        {/* ── LEFT: tenants ───────────────────────────────────────── */}
        <aside
          className={`rounded-xl border border-zinc-200 bg-white shadow-sm ${
            selectedTenantId ? "hidden lg:block" : "block"
          }`}
          data-testid="platform-users-tenants-pane"
        >
          <div className="border-b border-zinc-100 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                <Building2 className="h-3.5 w-3.5" />
                Tenants
              </div>
              <span className="text-[11px] tabular-nums text-zinc-500">
                {filteredTenants.length}
              </span>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input
                value={tenantSearch}
                onChange={(e) => setTenantSearch(e.target.value)}
                placeholder="Tenant ara..."
                className="pl-8"
                data-testid="platform-users-tenant-search"
              />
            </div>
          </div>
          <ul
            className="max-h-[calc(100vh-260px)] divide-y divide-zinc-100 overflow-y-auto"
            data-testid="platform-users-tenants-list"
          >
            {tenantLoading && (
              <li className="px-4 py-6 text-center text-sm text-zinc-500">
                {t("common.loading")}
              </li>
            )}
            {!tenantLoading && filteredTenants.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-zinc-400">
                Sonuç bulunamadı
              </li>
            )}
            {filteredTenants.map((tn) => {
              const isSelected = tn.id === selectedTenantId;
              return (
                <li key={tn.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedTenantId(tn.id)}
                    className={`flex w-full items-center gap-2.5 px-4 py-3 text-left transition-colors ${
                      isSelected
                        ? "bg-violet-50 shadow-[inset_3px_0_0_0_rgb(139,92,246)]"
                        : "hover:bg-zinc-50"
                    }`}
                    data-testid={`platform-users-tenant-item-${tn.id}`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-semibold ${
                        isSelected
                          ? "bg-violet-600 text-white"
                          : "bg-zinc-100 text-zinc-600"
                      }`}
                    >
                      {(tn.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div
                        className={`truncate text-sm ${
                          isSelected ? "font-semibold text-violet-900" : "text-zinc-800"
                        }`}
                      >
                        {tn.name}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                        <StatusChip status={tn.subscription_status} />
                        <span className="tabular-nums">{tn.user_count} kullanıcı</span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* ── RIGHT: users of selected tenant ─────────────────────── */}
        <section
          className={`rounded-xl border border-zinc-200 bg-white shadow-sm ${
            selectedTenantId ? "block" : "hidden lg:block"
          }`}
          data-testid="platform-users-panel"
        >
          {!selectedTenant ? (
            <EmptyRightPane />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTenantId(null)}
                  className="lg:hidden"
                  data-testid="platform-users-back-to-tenants"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="mr-auto min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-sm font-bold text-white">
                      {(selectedTenant.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-zinc-900">
                        {selectedTenant.name}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                        <UsersIcon className="h-3 w-3" />
                        <span>{selectedTenant.user_count} kullanıcı</span>
                        <span>·</span>
                        <span className="font-mono">
                          {selectedTenant.id.slice(0, 8)}...
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => setAddUserOpen(true)}
                  data-testid="platform-users-add-user-btn"
                  className="bg-violet-600 text-white hover:bg-violet-700"
                >
                  <UserPlus className="mr-1.5 h-4 w-4" />
                  Yeni Kullanıcı
                </Button>
              </div>

              {/* User-level filters */}
              <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 p-3">
                <div className="relative min-w-[180px] flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Ad veya e-posta ara..."
                    className="pl-8"
                    data-testid="platform-users-search"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger
                    className="h-9 w-36 text-xs"
                    data-testid="platform-users-role-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm roller</SelectItem>
                    <SelectItem value="super_admin">super_admin</SelectItem>
                    <SelectItem value="user">user</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={activeFilter} onValueChange={setActiveFilter}>
                  <SelectTrigger
                    className="h-9 w-32 text-xs"
                    data-testid="platform-users-active-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm durumlar</SelectItem>
                    <SelectItem value="true">Aktif</SelectItem>
                    <SelectItem value="false">Pasif</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Users table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50/70 text-[11px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="w-10 px-3 py-2.5">
                        <Checkbox
                          data-testid="platform-users-select-all"
                          checked={
                            users.length > 0 &&
                            users.every((u) => selectedIds.has(u.id))
                          }
                          onCheckedChange={(v) => toggleSelectAll(!!v)}
                          aria-label="Tümünü seç"
                        />
                      </th>
                      <th className="px-4 py-2.5 text-left font-semibold">Kullanıcı</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Rol</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Durum</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Aksiyonlar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {userLoading && (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-sm text-zinc-500">
                          {t("common.loading")}
                        </td>
                      </tr>
                    )}
                    {!userLoading && users.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-sm text-zinc-400">
                          Bu tenant&apos;ta eşleşen kullanıcı yok
                        </td>
                      </tr>
                    )}
                    {users.map((u) => (
                      <tr
                        key={u.id}
                        className={`hover:bg-zinc-50/60 ${
                          selectedIds.has(u.id) ? "bg-violet-50/40" : ""
                        }`}
                        data-testid={`platform-user-row-${u.id}`}
                      >
                        <td className="w-10 px-3 py-2.5">
                          <Checkbox
                            data-testid={`platform-user-select-${u.id}`}
                            checked={selectedIds.has(u.id)}
                            onCheckedChange={() => toggleSelect(u.id)}
                            aria-label={`${u.email} seç`}
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-xs font-semibold text-white">
                              {(u.name || u.email).charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-zinc-900">
                                {u.name || "—"}
                              </div>
                              <div className="truncate text-[11px] text-zinc-500">
                                {u.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                            <SelectTrigger
                              className="h-8 w-32 text-xs"
                              data-testid={`platform-user-role-${u.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="super_admin">super_admin</SelectItem>
                              <SelectItem value="user">user</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2.5">
                          {u.active ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-200">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-700 ring-1 ring-zinc-200">
                              <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" />
                              inactive
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditUser(u)}
                              data-testid={`platform-user-edit-${u.id}`}
                              className="h-8 px-2 text-xs"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 px-2"
                                  data-testid={`platform-user-menu-${u.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem
                                  onClick={() => setResetUser(u)}
                                  data-testid={`platform-user-reset-pw-${u.id}`}
                                >
                                  <KeyRound className="mr-2 h-4 w-4 text-amber-600" />
                                  Şifreyi Sıfırla
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setEditUser(u)}
                                  data-testid={`platform-user-edit-menu-${u.id}`}
                                >
                                  <Pencil className="mr-2 h-4 w-4 text-violet-600" />
                                  Düzenle
                                </DropdownMenuItem>
                                {u.active ? (
                                  <DropdownMenuItem onClick={() => setActive(u, false)}>
                                    <UserX className="mr-2 h-4 w-4 text-red-600" />
                                    Pasifleştir
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => setActive(u, true)}>
                                    <UserCheck className="mr-2 h-4 w-4 text-emerald-600" />
                                    Aktifleştir
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => deleteOne(u)}
                                  className="text-red-600 focus:text-red-700"
                                  data-testid={`platform-user-delete-${u.id}`}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Sil
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Floating bulk-delete bar */}
      {selectedIds.size > 0 && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4"
          data-testid="platform-users-bulk-bar"
        >
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-zinc-800/10 bg-zinc-900 px-4 py-2 text-white shadow-lg shadow-zinc-950/20">
            <span className="text-sm font-medium">
              <span className="tabular-nums" data-testid="platform-users-selected-count">
                {selectedIds.size}
              </span>{" "}
              kullanıcı seçili
            </span>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-zinc-300 underline-offset-4 hover:text-white hover:underline"
              data-testid="platform-users-clear-selection"
            >
              Seçimi kaldır
            </button>
            <div className="mx-1 h-4 w-px bg-white/20" />
            <Button
              size="sm"
              onClick={() => setConfirmBulkDelete(true)}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="platform-users-bulk-delete-btn"
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Toplu Sil
            </Button>
          </div>
        </div>
      )}

      <BulkDeleteDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        kind="user"
        count={selectedIds.size}
        onConfirm={runBulkDelete}
      />

      <UserEditDialog
        open={!!editUser}
        onOpenChange={(v) => !v && setEditUser(null)}
        user={editUser}
        onSaved={loadUsers}
      />

      <UserPasswordResetDialog
        open={!!resetUser}
        onOpenChange={(v) => !v && setResetUser(null)}
        user={resetUser}
      />

      <TenantAddUserDialog
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
        tenant={selectedTenant}
        onCreated={() => {
          loadUsers();
          loadTenants();
        }}
      />
    </div>
  );
}

function EmptyRightPane() {
  return (
    <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-2 p-10 text-center text-sm text-zinc-500">
      <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-violet-50 text-violet-600">
        <Building2 className="h-6 w-6" />
      </div>
      <div className="font-medium text-zinc-800">Bir tenant seç</div>
      <div className="max-w-xs text-zinc-500">
        Sol taraftan bir tenant seçtiğinde, o tenant&apos;ın kullanıcılarını
        burada görüp yönetebilirsin.
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    trial: "bg-blue-100 text-blue-700",
    active: "bg-emerald-100 text-emerald-700",
    past_due: "bg-amber-100 text-amber-700",
    suspended: "bg-orange-100 text-orange-700",
    canceled: "bg-zinc-100 text-zinc-600",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
        map[status] || "bg-zinc-100 text-zinc-600"
      }`}
    >
      {status || "—"}
    </span>
  );
}
